import React, { FC, useEffect, useRef, useState } from 'react';
import { Button, Input, Select } from 'antd';
import { Divider } from 'common/components';
import { getId, mapToMenuWithDivider, showSuccessMessage } from 'common/helpers';
import { Close, Pin, Plus } from 'common/svg';
import { AuthHooks, UserHooks } from 'common/hooks';
import { IOption } from 'common/models';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { AddressCreateCard } from 'entities/Address/components';
import { IAddress, ICreateAddressPayload } from 'entities/Address/Address.models';
import { createAddress, selectIsAddressLoading } from 'entities/Address/Address.slice';
import { addSubdivisionAddress, selectIsSubdivisionLoading, selectSubdivision } from 'entities/Subdivision/Subdivision.slice';

interface IAntdFormControl {
  onChange?: (value?: any) => void;
  value?: any;
}

interface IComponentProps {
  onAddressChange?: (address: IAddress) => void;
}

type AllType = IComponentProps & IAntdFormControl;

export const DeliveryAddressSelector: FC<AllType> = ({ value, onChange, onAddressChange }) => {
  const dispatch = useAppDispatch();

  const isAddressLoading = useAppSelector(selectIsAddressLoading);
  const subdivision = useAppSelector(selectSubdivision);
  const isSubdivisionLoading = useAppSelector(selectIsSubdivisionLoading);

  const [search, setSearch] = useState<string>('');
  const [addressId, setAddressId] = useState<number | null>(null);
  const [openAddressSelector, setOpenAddressSelector] = useState<boolean>(false);
  const [openCreateAddressCard, setOpenCreateAddressCard] = useState<boolean>(false);
  const createAddressInFlightRef = useRef<boolean>(false);
  const { isSeller } = AuthHooks.useIsSeller();
  const { isAccountAdmin } = UserHooks.useCurrentUserRole();

  const allowToCreateAddress = !isSeller && isAccountAdmin;
  const addresses = subdivision?.addresses ?? [];
  const addressOptions: IOption[] = mapToMenuWithDivider(addresses.map((item) => ({ label: item.name, value: item.id })));

  useEffect(() => {
    setAddressId(value);
  }, [value]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearch(e.target.value);
  };

  const toggleOpenAddressSelector = (open: boolean): void => {
    setSearch('');
    setOpenAddressSelector(open);
  };

  const handleOpenCreateAddressCard = (): void => {
    setOpenCreateAddressCard(true);
  };

  const closeCreateAddressCard = (): void => {
    setOpenCreateAddressCard(false);
  };

  const handleCreateAddress = async ({ onSuccess, ...rest }: ICreateAddressPayload): Promise<void> => {
    if (!subdivision) {
      return;
    }

    // Синхронный guard от двойного сабмита: loading-state асинхронен, даблклик его проскакивает
    // (создаются два адреса + гонка main в подразделении).
    if (createAddressInFlightRef.current) {
      return;
    }
    createAddressInFlightRef.current = true;

    try {
      const address = await dispatch(
        createAddress({
          ...rest,
          onSuccess: () => showSuccessMessage('Новый адрес добавлен в аккаунт.'),
        }),
      );

      if (!address) {
        return;
      }

      await dispatch(
        addSubdivisionAddress({
          subdivisionId: subdivision.id,
          addressId: address.id,
          onSuccess: () => showSuccessMessage('Новый адрес добавлен в подразделение.'),
        }),
      );

      onSuccess?.();
      onChange?.(address.id);
      onAddressChange?.(address);
    } finally {
      createAddressInFlightRef.current = false;
    }
  };

  const changeAddress = (addressId: number): void => {
    const address = addresses.find((i) => i.id === addressId);

    if (!address) {
      return;
    }

    onChange?.(address.id);
    onAddressChange?.(address);
  };

  return (
    <div>
      <Select
        open={openAddressSelector}
        onDropdownVisibleChange={toggleOpenAddressSelector}
        rootClassName="redesign"
        placeholder="Выберите адрес доставки"
        virtual={false}
        suffixIcon={<Pin className="icon-dark-grey" />}
        notFoundContent="В списке нет вариантов."
        optionLabelProp="label"
        value={value}
        onChange={changeAddress}
        dropdownRender={(menu) => {
          return (
            <>
              <div className="mb-6">
                <Input
                  allowClear={{ clearIcon: <Close className="icon-close-input" /> }}
                  value={search}
                  onChange={handleSearch}
                />
              </div>

              {allowToCreateAddress && (
                <div>
                  <Button
                    className="button-text dark-green"
                    icon={<Plus className="icon-plus-dark-green" />}
                    onClick={handleOpenCreateAddressCard}
                  >
                    Добавить адрес
                  </Button>

                  <Divider marginY={20} />
                </div>
              )}

              {menu}
            </>
          );
        }}
      >
        {addressOptions.map((item) => {
          const address = addresses.find((a) => a.id === item.value);
          const selected = item.value === addressId;

          if (item.type === 'divider') {
            return <Select.Option key={getId()} type="divider" children={null} />;
          }

          return (
            <Select.Option key={item.value} value={item.value} label={item.label}>
              {address ? (
                <div>
                  <div className={`${selected ? 'ff-medium' : 'ff-regular'} fs-m`}>{address.name}</div>

                  {address.isMain && <div className="mt-3 ff-regular fs-xs color-dark-grey">Основной адрес</div>}
                </div>
              ) : (
                item.label
              )}
            </Select.Option>
          );
        })}
      </Select>

      <AddressCreateCard
        open={openCreateAddressCard}
        loading={isAddressLoading || isSubdivisionLoading}
        onCancel={closeCreateAddressCard}
        onSubmit={handleCreateAddress}
      />
    </div>
  );
};
